import { Datapoint, VariableSlug } from '@/api/generated/model';
import { ITimeRange } from '@/api/requests/types/common.type';
import { WithDashedLine } from '@/types/chart';

export interface IChartShort {
  kind: string;
  time_range: ITimeRange;
  variable: string | null;
  filters: IChartFilterRequest[];
  breakdown: string | null;
  settings?: IGetSettingsOptions | null;
}

export interface IChart extends IChartShort {
  id: string;
  name: string;
  description: string;
  board_name: string;
  board_id: string;
}

export interface IChartFilterShort {
  slug: string;
  display_name?: string;
  description?: string;
  kind?: string;
}

export interface IFilterOperation {
  slug: string;
  display_name: string;
}

export interface IChartFilter extends IChartFilterShort {
  allowed_operations: IFilterOperation[];
}

export interface IChartFilterRequest extends IChartFilterShort {
  operation: string | null;
  values: string[] | null;
  provider: string;
}

export interface IGetChartDataParams {
  kind: string;
  variable: string;
  filters: IGetChartDataParamsFilters[];
  time_range: ITimeRange;
  settings?: IGetChartDataParamsSettings | null;
  breakdown: string | null;
  providers: string[];
}

export interface IGetChartDataParamsFilters {
  slug: string;
  operation: string;
  values: string[];
  provider: string;
}

export interface IGetChartDataParamsSettings {
  granularity: TGranularity;
  visualization: TVisualization;
}

export interface IGetSettingsOptions {
  granularity: TGranularity;
  visualization: TVisualization;
}

export type ChartDataset = WithDashedLine & {
  // the label of the chart element (line, bar, etc.)
  label: string;
  data: (number | null | Datapoint)[];
  extras?: Record<string, unknown>;
};

export enum EChartKind {
  LINE = 'line_chart',
  AREA = 'area_chart',
  BAR_CHART = 'vertical_bar',
  HORIZONTAL_BAR_CHART = 'horizontal_bar',
  CLUSTERED_BAR_CHART = 'clustered_bar',
  METRIC = 'metric',
  DISTRIBUTED = 'distributed',
  SCATTER = 'scatter',
  PIE = 'pie',
  GAUGE = 'gauge',
}

export type ChartKind =
  | 'line_chart'
  | 'area_chart'
  | 'vertical_bar'
  | 'horizontal_bar'
  | 'clustered_bar'
  | 'metric'
  | 'gauge'
  | 'distributed'
  | 'scatter'
  | 'pie';
export const chartTypeMap = {
  line: 'line_chart',
  area: 'area_chart',
  stackedBar: 'vertical_bar',
  horizontalBar: 'horizontal_bar',
  clusteredBar: 'clustered_bar',
  metric: 'metric',
  gauge: 'gauge',
  distributed: 'distributed',
  scatter: 'scatter',
  pie: 'pie',
} as const satisfies Record<string, ChartKind>;

export type TVisualization = 'cumulative' | 'rolling' | 'linear' | 'average' | 'median' | 'min' | 'max' | 'formula';

export type TGranularity = 'minute' | 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';
export const granularityMap = {
  minute: 'minute',
  hour: 'hour',
  day: 'day',
  week: 'week',
  month: 'month',
  quarter: 'quarter',
  year: 'year',
} as const satisfies Record<string, TGranularity>;

export type TDistributionRange = 'day' | 'week' | 'month' | 'year';
export const distributionRangeMap = {
  day: 'day',
  week: 'week',
  month: 'month',
  year: 'year',
} as const satisfies Record<string, TDistributionRange>;

export enum EAggregatedDataType {
  COST = 'cost',
  USERS = 'users',
  LATENCY = 'latency',
  PERCENTAGE = 'percentage',
  TIMES = 'times',
  INTERACTIONS = 'interactions',
  SATISFACTION = 'satisfaction',
  FRUSTRATION = 'frustration',
  WARNINGS = 'warnings',
  CONVERSATIONS = 'conversations',
  CONVERSATION_LENGTH = 'conversation_length',
  CONVERSATION_TIME = 'conversation_time',
  SENTIMENT = 'sentiment',
  N_INTENTS = 'intents',
  ERROR_RATE = 'error_rate',
  BOUNCE_RATE = 'bounce_rate',
  ERROR_RATE_EXPLICIT = 'error_rate_explicit',
  RETURN_RATE = 'return_rate',
  PENETRATION = 'penetration',
}

export const variablesMap = {
  cost: 'cost',
  users: 'users',
  latency: 'latency',
  interactions: 'interactions',
  satisfaction: 'satisfaction',
  frustration: 'frustration',
  warnings: 'warnings',
  conversations: 'conversations',
  conversationLength: 'conversation_length',
  promptsToValue: 'prompts_to_value',
  conversationTime: 'conversation_time',
  sentiment: 'sentiment',
  nIntents: 'intents',
  errorRate: 'error_rate',
  bounceRate: 'bounce_rate',
  errorRateExplicit: 'error_rate_explicit',
  returnRate: 'return_rate',
} as const satisfies Record<string, VariableSlug>;
